import { useEffect, useMemo, useRef, useState,  } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Button, Typography, TextField, Stack, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { deleteDevice } from 'services/apiService';
import useToken from 'services/useToken';
import { use } from 'react';
import { showAppAlert } from 'store/reducers/appreducer';

const PAGE_SIZE = 10;

const SelectDeviceTable = ({onDeviceSelect, handleClose}) => {
  const { appUser } = useSelector((state) => state.appreducer);
  const mapPageToNextCursor = useRef({});
  const [loading, setLoading] = useState(true);
  const { deviceList, selectedDevice } = useSelector((state) => state.appreducer);
  const [devicesData, setDevicesData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = useToken()
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  const handleDialogOpen = (device) => {
    setDeviceToDelete(device);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDeviceToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (deviceToDelete) {
      // console.log(`Deleting device with id: ${deviceToDelete.id}`);
      // Add your delete logic here
      const data = {
        device_imei: deviceToDelete.id,
      }
      
      await deleteDevice(data, token.token)
        .then((res) => {
          // console.log('response from delete device ->', res);
          if(res && res.status === 200) {
            const message = res.message || 'Device deleted successfully!';
            dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: message }));
          }
          handleClose();
          handleDialogClose();
        })
        .catch((e) => {
          console.log('error occured', e);
          const message = e.message || 'An error occurred during deleting device!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: message }));
        });
      /* if (response){
        console.log('response delete Device -->', response);
        handleDialogClose();
      } */
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const filteredDevices = useMemo(() => {
    if (!searchText) {
      return deviceList;
    }
    const lowerSearchText = searchText.toLowerCase();
    return deviceList.filter((device) => {
      const fullDetails = `${device.IMEI} ${device.model} ${device.name}`.toLowerCase();
      return fullDetails.includes(lowerSearchText);
    });
  }, [deviceList, searchText]);

  useEffect(() => {
    setRowCountState(filteredDevices.length);
    setDevicesData(
      filteredDevices.map((device, index) => ({
        id: device.id,
        name: device.name,
        model: device.model,
        IMEI: device.IMEI
      }))
    );
  }, [filteredDevices]);

  const [pageInfo, setPageInfo] = useState({
    totalRowCount: 0,
    nextCursor: ''
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE
  });

  const columns = [
    { field: 'IMEI', headerName: 'IMEI', width: 150 },
    { field: 'model', headerName: 'Model', width: 100 },
    { field: 'name', headerName: 'Name', width: 200 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 240,
      cellClassName: 'actions',
      sortable: false,
      disableClickEventBubbling: true,    
      renderCell: (params) => {
        const onSelectClick = (e) => {
          const currentRow = params.row;
          if (currentRow.id === selectedDevice) {
            return;
          }
          return onDeviceSelect(currentRow.id);
        };

        const onDeleteClick = (e) => {
          // Add your delete logic here
          const currentRow = params.row;
          console.log(`Deleting device with id: ${currentRow.id}`);
          handleDialogOpen(currentRow);
        };

        return (
          <>
            <Button variant="outlined" color={params.row.id === selectedDevice ? 'success' : 'primary'} size="small" onClick={onSelectClick}>
              {params.row.id === selectedDevice ? 'Selected' : 'Select'}
            </Button>
            {appUser.user_type !== 'ORG_ADMIN' && appUser.user_type !== 'SUPER_ADMIN' && (
              <Button variant="outlined" color="error" size="small" onClick={onDeleteClick} sx={{ ml: 1 }}>
                Delete
              </Button>
            )}
          </>
        );
      }
    }
  ];

  const columnsMobile = [
    {
      field: 'fullDetails',
      headerName: 'Device Details',
      width: 200,
      disableColumnFilter: false, // Enable search for this column
      renderCell: (params) => (
        <div>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {params.row.IMEI}
          </Typography>
          <Typography variant="caption">{`${params.row.name}`}</Typography>
          <br></br>
          <Typography variant="caption">{`${params.row.model}`}</Typography>
        </div>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const onClick = (e) => {
          const currentRow = params.row;
          // return alert(JSON.stringify(currentRow, null, 4));
          return onDeviceSelect(currentRow.id);
        };

        return (
        <Button variant="outlined" color="primary" size="small" onClick={onClick}>
            Select
        </Button>
        );
      }
    }
  ];

  const displayedColumns = isMobile ? columnsMobile : columns;
  const rowHeight = isMobile ? 80 : 48;
  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCountState` from being undefined during the loading
  const [rowCountState, setRowCountState] = useState(pageInfo?.totalRowCount || 0);

  const handlePaginationModelChange = (newPaginationModel) => {
    // We have the cursor, we can allow the page transition.
    if (newPaginationModel.page === 0 || mapPageToNextCursor.current[newPaginationModel.page - 1]) {
      setPaginationModel(newPaginationModel);
    }
  };

  useEffect(() => {
    if (!loading && pageInfo?.nextCursor) {
      // We add nextCursor when available
      mapPageToNextCursor.current[paginationModel.page] = pageInfo?.nextCursor;
    }
  }, [paginationModel.page, loading, pageInfo?.nextCursor]);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (pageInfo?.totalRowCount !== undefined ? pageInfo?.totalRowCount : prevRowCountState));
  }, [pageInfo?.totalRowCount, setRowCountState]);

  useEffect(() => {
    if (deviceList.length > 0) {
      setLoading(false);
    }
    setDevicesData(deviceList.map((device, index) => ({ ...device, id: device.id ?? index })));
    setPageInfo({ totalRowCount: deviceList.length, nextCursor: '' });
  }, [deviceList]);
  
  return (
    <>
      <TextField label="Search Devices" variant="outlined" fullWidth value={searchText} onChange={handleSearchChange} sx={{ mb: 2 }} />
      {devicesData.length === 0 && !loading ? (
        <Stack height={80} width={708.444} alignItems="center" justifyContent="center">
          <Typography>No Results Found</Typography>
        </Stack>
      ) : (
        <DataGrid
          rows={devicesData}
          columns={displayedColumns}
          loading={loading}
          rowHeight={rowHeight}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          initialState={{
            filter: {
              filterModel: {
                items: []
              }
            }
          }}
          slots={{
            toolbar: GridToolbar
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
            }
          }}
          sx={{
            mt: 2,
            fontSize: 14,
            borderColor: 'secondary.light',
            '& .MuiDataGrid-row:hover': {
              bgcolor: 'primary.lighter'
            },
            '& .MuiDataGrid-row.Mui-selected': {
              color: 'primary.main',
              bgcolor: 'primary.lighter'
            },
            '& .MuiTablePagination-displayedRows': {
              margin: 0
            },
            '& .MuiDataGrid-withBorderColor': {
              borderColor: 'secondary.light'
            }
          }}
        />
      )}
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this device?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="primary" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
};

export default SelectDeviceTable;
